import React from 'react';
import { Label, ListBox, Select } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type SelectOption = { id: string; name: string; depth?: number };

type LabeledSelectProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    slot: string;
    placeholder?: string;
};

/** Single-select with a HeroUI label, matching the page's other controls. */
export const LabeledSelect: React.FC<LabeledSelectProps> = ({
    label,
    value,
    onChange,
    options,
    slot,
    placeholder,
}) => (
    <Select
        fullWidth
        onChange={(next) => onChange(String(next))}
        placeholder={placeholder}
        value={value}
        variant="secondary"
    >
        <Label
            id={`manual-import-select-label-${slot}`}
            {...getNodeTestIdProp(`manual-import-select-label-${slot}`)}
        >
            {label}
        </Label>
        <Select.Trigger
            // data-testid only: an explicit `id` on Select.Trigger sends react-
            // aria's id-merging into an endless setState loop ("Maximum update
            // depth exceeded") that also starves router transitions, so SPA
            // navigation away from the page never commits.
            {...getNodeTestIdProp(`manual-import-select-trigger-${slot}`)}
        >
            <Select.Value />
            <Select.Indicator />
        </Select.Trigger>
        <Select.Popover
            {...getNodeTestIdProp(`manual-import-select-popover-${slot}`)}
        >
            <ListBox>
                {options.map((option) => (
                    <ListBox.Item
                        id={option.id}
                        key={option.id}
                        textValue={option.name}
                        {...getNodeTestIdProp(
                            `manual-import-select-option-${slot}-${option.id}`,
                        )}
                    >
                        <span
                            style={{
                                paddingInlineStart: option.depth
                                    ? `${option.depth * 12}px`
                                    : undefined,
                            }}
                        >
                            {option.name}
                        </span>
                        <ListBox.ItemIndicator />
                    </ListBox.Item>
                ))}
            </ListBox>
        </Select.Popover>
    </Select>
);
